'use strict';
/* ==========================================================================
   levels/piece.js — "It's All Greek (and Latin) to Me", step 4: TAKE IT APART (DESIGN.md §5.5; content/mic.json),
   orientation "any". The karaoke of the spoken-word piece, with the words it names cut apart on the cutting mat.

   ► The recording (made later by the assets agent; NOTHING here changes when it lands):
       assets/music/piece.m4a          lesson.json preload.stream: played through the shared <audio> element (Audio.media)
       assets/music/piece_words.json   tools/song_timing.py, the house format (as lessons/narrator/assets/music/mic_words.json):
         { duration, lines: [{ i, section, text, s, e, words: [{ w, s, e }] }], beats?: {bpm, offset} }
       Only lines with words are shown. The scene is SYNCED only when BOTH files load.
   ► Without them (today): the FALLBACK. The lyric lines of content/mic.json advance evenly over mic.duration (each
     non-empty line gets an equal slice; section tags take no time), the words of a line light at an even pace, and a
     label says so ("no recording yet: reading along · words at an even pace, not synced"). Play / pause / seek /
     restart work on this virtual clock; one full listen = the virtual clock reached the end.

   The frame is a pure function of PIECE TIME t (the <audio> clock while it plays, else a wall clock): the mat sets the
   engine clock T = t while it draws, so the kit's cut animations (cutT), glosses and stamps are seekable.

   The mat (content from the model only: core-local/words.js through the kit WK), chosen from the CURRENT LINE's text:
     astro → astronaut, therm → thermometer, bene → benefactor (cut at the model's cuts, each cut on the word that
       names the part after it, else the part before it + 0.4 s, else the line start; glosses type on)
     pedestrian (Latin ped, uncut: the model does not cut it) / pediatrician (Greek paed, cut): "ped" marked in each
       part's language ink, the pedestrian strip kept above the pediatrician one so the two inks sit together
     "in can be ..." → the two in- tiles of the model's in trap (Words.trap('in').bins), flipping to their meanings
       on the words that say them; inflammable → an uncut strip with the in- part the trap files it under
     tale words (Words.tales ids named in the line: muscle / disaster, posh / SOS) → their cards, stamped with the
       model's verdict; helico → helicopter cut (helico | pter)
     a line that names nothing keeps the last picture; intro / chorus / outro: the calm mat (the title in cut-out
       letters, the last word reassembled under it).
   Karaoke band (bottom): the section as a small red heading, the current line in typewriter type (spoken words ink,
   the active word under the neon highlighter), the next line faint; the position strip (click / drag to seek).
   Controls: ⌂ map (HUD), play / pause and restart drawn as paths (HUD slots 1, 2), the strip. Keys: Space play /
   pause, R restart, ← → 5 s, Enter (at the end) back to the map; Escape = the map (engine). "Finish" after ≥ 90 %
   heard; the end after ≥ 90 % heard stamps the step (Game.complete('piece')).
   Portrait phone: the mat fills the stage; Dock.karaoke + the label + the strip + ↺ ▶ ⌂ in the dock.

   QA: sc.song ({dur, lines, virtual}), sc.mode ('wait' | 'fallback' | 'synced'), sc.now(), sc.play(t), sc.pause(),
   sc.toggle(), sc.seek(t), sc.restart(), sc.debugAt(t), sc.done(), sc.canFinish(), sc.heard, sc.ended,
   sc.lyricAt(t), sc.activeWord(t), sc.planAt(t); Levels.piece.dbg (what the last frame drew: mode, label, t, line,
   word, section, picture {kind, strips: [{id, made, model, plain, glosses, marks}], tiles, tales}); static
   Levels.piece.finish() (stamps now, as the stub did: tools/integration.js, tools/qa.js).
   ========================================================================== */
(function () {
  const STEP = 'piece';
  const AUDIO = 'music/piece.m4a', WORDS = 'music/piece_words.json', KEY = 'song:piece';
  const THRESH = 0.9;
  const CREDIT = 'AI-generated performance';
  const NOREC = 'no recording yet: reading along · words at an even pace, not synced';
  const TYPE = "'Special Elite', 'Courier New', monospace";
  const BAND_Y = 780, CUR_Y = 868, NEXT_Y = 958;
  const MAT = { x: 150, y: 196, w: 1620, h: 566 }, MAT_D = { x: 50, y: 40, w: 1820, h: 1000 };
  const ZC = () => (window.Zine ? Zine.C : { ink: '#141210', paper: '#f7f5ef', red: '#d9352c', grey: '#6d6a64' });
  const GREY = '#8d897f';
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  const c01 = x => Math.max(0, Math.min(1, x));
  const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');

  /* ---------------- the sheet (content/mic.json) and the two clocks ---------------- */
  function sheet() {
    const raw = String((Game.content && Game.content.mic && Game.content.mic.lyrics) || ''), out = [];
    let sec = '';
    for (const line of raw.split('\n')) {
      const s = line.trim(), m = /^\[(.+)\]$/.exec(s);
      if (m) sec = m[1]; else if (s) out.push({ section: sec, text: s });
    }
    return out;
  }
  /* the fallback: each lyric line an equal slice of mic.duration; its words at an even pace */
  function fallbackSong() {
    const mic = (Game.content && Game.content.mic) || {}, L = sheet(), dur = +mic.duration || 82, sl = dur / Math.max(1, L.length);
    const lines = L.map((l, i) => {
      const s = i * sl, e = s + sl - 0.3, ws = l.text.split(/\s+/).filter(Boolean), d = (e - s) / ws.length;
      return { i, section: l.section, text: l.text, s, e, words: ws.map((w, j) => ({ w, s: s + j * d, e: s + (j + 1) * d - 0.03 })) };
    });
    return finishSong({ dur, lines, virtual: true });
  }
  function realSong(d) {
    if (!d || !Array.isArray(d.lines)) throw new Error('bad timings');
    const lines = d.lines.filter(l => l && l.words && l.words.length).map((l, i) => ({ i, section: l.section || '', text: l.text || l.words.map(w => w.w).join(' '), s: l.s != null ? l.s : l.words[0].s, e: l.e != null ? l.e : l.words[l.words.length - 1].e, words: l.words }));
    if (!lines.length) throw new Error('empty timings');
    return finishSong({ dur: d.duration || lines[lines.length - 1].e + 2, lines, virtual: false });
  }
  function finishSong(S) {
    S.plan = plan(S);
    // the end of the spoken part (the recording's beat plays on after it): the finale and Finish come then
    S.voiceEnd = Math.min(S.dur, Math.max(...S.lines.map(l => l.e)));
    S.decodedAt = Math.min(S.dur - 0.5, S.voiceEnd + 1.2);
    // the words the piece took apart, in order (for the finished mat)
    const seen = new Set(); S.finale = [];
    for (const e of S.plan) for (const x of [e && e.above, e]) if (x && x.kind === 'strip' && !seen.has(x.id)) { seen.add(x.id); S.finale.push(x); }
    S.ticks = [];
    S.lines.forEach((l, k) => { if (k > 0 && l.section !== S.lines[k - 1].section) S.ticks.push(l.s - 0.3); });
    return S;
  }

  /* a line comes up 0.5 s before its first word, never before the previous line ends (narrator's rule) */
  const appearAt = (L, k) => (k === 0 ? L[0].s - 0.5 : Math.min(L[k].s, Math.max(L[k].s - 0.5, L[k - 1].e + 0.05)));
  function lineIdx(S, t) { let cur = -1; for (let k = 0; k < S.lines.length; k++) if (t >= appearAt(S.lines, k)) cur = k; return cur; }
  function lyricAt(S, t) {
    const k = lineIdx(S, t), cur = k >= 0 ? S.lines[k] : null, nx = S.lines[k + 1] || null;
    return { k, cur, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(S, t) {
    const k = lineIdx(S, t); if (k < 0) return null;
    const ws = S.lines[k].words;
    for (let j = 0; j < ws.length; j++) {
      // a very short word ("and" in 40 ms) still lights for 0.12 s; the next word then lights at most 0.12 s late
      const nx = ws[j + 1], end = Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? Math.max(nx.s - 0.02, ws[j].s + 0.12) : 1e9);
      if (t >= ws[j].s - 0.02 && t < end) return { line: k, j, w: ws[j].w, s: ws[j].s, e: ws[j].e };
    }
    return null;
  }

  /* ---------------- the plan: what the mat shows on each line (from its text + the model) ---------------- */
  const has = id => { try { return !!(window.Words && Words.word(id)); } catch (e) { return false; } };
  const safe = (fn, dflt) => { try { const v = fn(); return v == null ? dflt : v; } catch (e) { return dflt; } };
  /* the time of the first word of line l (from word j0) that matches pred, or null */
  function cue(l, pred, j0 = 0) { for (let j = j0; j < l.words.length; j++) if (pred(normW(l.words[j].w))) return l.words[j].s; return null; }
  const starts = lab => w => !!lab && w.startsWith(normW(lab));
  /* the in- part the model's in trap files inflammable under (Words.trap('in')) */
  function trapPart(trap, wordId) {
    const T_ = safe(() => Words.trap(trap), null); if (!T_) return null;
    const w = T_.words.find(x => x.id === wordId), b = w && T_.bins.find(x => x.id === w.bin);
    return b ? b.part : null;
  }
  function stripEntry(l, id, partId) {
    if (!has(id)) return null;
    const W = Words.word(id), e = { kind: 'strip', id, made: [], cutT: {}, model: [], marks: [] };
    if (W.cuttable) {
      const cuts = safe(() => Words.cuts(id), []), bs = safe(() => Words.boundaries(id), []);
      e.model = cuts.slice(); e.made = cuts.slice();
      let last = l.s + 0.3;
      cuts.forEach((p, k) => {
        const b = bs[k] || {};
        let t = cue(l, starts(safe(() => Words.label(b.right), '')));
        if (t == null) { const tl = cue(l, starts(safe(() => Words.label(b.left), ''))); t = tl != null ? tl + 0.4 : null; }
        if (t == null) t = last;
        t = Math.max(t, l.s); last = t + 0.25; e.cutT[p] = t;
      });
    }
    if (partId && safe(() => Words.part(partId), null)) {
      const tw = cue(l, w => w.startsWith(normW(W.word)));
      e.marks.push({ id: partId, at: tw != null ? tw : l.s + 0.3, color: WK.inkOf(Words.language(partId)), label: Words.short(partId) });
    }
    return e;
  }
  /* the Words.tales ids a line names: a word that is the id (muscle's → muscle), or single letters spelling it (S O S) */
  function talesIn(l) {
    const ids = safe(() => Words.tales(), []), ws = l.words.map(w => normW(w.w)), out = [];
    for (const id of ids) {
      let at = null;
      for (let j = 0; j < ws.length && at == null; j++) {
        if (ws[j] === id || (ws[j].startsWith(id) && ws[j].length <= id.length + 1)) at = l.words[j].s;
        else if (ws[j].length === 1) { let s = ''; for (let k = j; k < ws.length && ws[k].length === 1; k++) { s += ws[k]; if (s === id) { at = l.words[k].s; break; } } }
      }
      if (at != null) out.push({ id, at, verdict: safe(() => Words.tale(id).verdict, null) });
    }
    return out;
  }
  const RULES = [
    { re: /\bastro/i, word: 'astronaut' },
    { re: /\btherm/i, word: 'thermometer' },
    { re: /\bbene\b/i, word: 'benefactor' },
    { re: /pedestrian/i, word: 'pedestrian', part: () => 'ped' },
    { re: /pediatrician/i, word: 'pediatrician', part: () => 'paed', keep: 'pedestrian' },
    { re: /\bin can be\b/i, tiles: 'in' },
    { re: /inflammable/i, word: 'inflammable', part: () => trapPart('in', 'inflammable') },
    { re: /helico/i, word: 'helicopter' },
  ];
  const CALM = /^(intro|outro|chorus)/i;
  function plan(S) {
    const out = [];
    let prev = null, lastStrip = null;
    S.lines.forEach((l, k) => {
      let e = null;
      const tl = window.Words ? talesIn(l) : [];
      if (tl.length) e = { kind: 'tales', tales: tl };
      for (const r of RULES) {
        if (e || !r.re.test(l.text) || !window.Words) continue;
        if (r.tiles) {
          const bins = safe(() => Words.trap(r.tiles).bins, []);
          if (bins.length) e = { kind: 'tiles', tiles: bins.map((b, i) => {
            const P = safe(() => Words.part(b.part), null), last = P ? normW(String(P.short).split(',').pop()) : '';
            const j0 = l.words.findIndex(w => normW(w.w) === normW(Words.label(b.part)));
            let at = cue(l, w => w === last, j0 >= 0 ? j0 + 1 : 0);
            if (at == null) at = l.s + 0.6 + i * 0.8;
            return { part: b.part, at };
          }) };
        } else {
          e = stripEntry(l, r.word, r.part && r.part());
          if (e && r.keep && prev && prev.kind === 'strip' && prev.id === r.keep) e.above = prev;
        }
      }
      if (!e && CALM.test(l.section || '')) {
        e = prev && prev.kind === 'title' && prev.sec === l.section && prev.last === lastStrip ? prev
          : { kind: 'title', last: lastStrip, sec: l.section };
      }
      if (!e) e = prev || { kind: 'title', last: null };
      if (e !== prev) e.from = l.s - 0.35;
      if (e.kind === 'strip') lastStrip = e.id;
      out[k] = e; prev = e;
    });
    return out;
  }

  /* ---------------- the mat ---------------- */
  const layouts = new Map();
  function layoutOf(id, box, o) {
    const k = id + '|' + box.x + '|' + box.w + '|' + box.cy + '|' + o.maxPitch + '|' + o.maxH + '|' + UI.text(26);
    let L = layouts.get(k);
    if (!L) { L = WK.layout(id, box, { display: true, ...o }); if (L) { if (layouts.size > 60) layouts.clear(); layouts.set(k, L); } }
    return L;
  }
  /* one strip entry on the mat at cy; returns what was drawn (for dbg) */
  function drawEntryStrip(e, t, M, cy, o = {}) {
    const box = { x: M.x + 70, w: M.w - 140, cy };
    const L = layoutOf(e.id, box, { maxPitch: o.maxPitch || 112, maxH: o.maxH || 134, cuts: o.uncut ? 0 : undefined });
    if (!L) return null;
    const made = o.uncut ? [] : e.made.filter(p => t >= e.cutT[p]);
    const st = { made, cutT: o.uncut ? {} : e.cutT };
    const marks = o.uncut ? [] : e.marks.filter(m => t >= m.at).map(m => ({ id: m.id, color: m.color, fill: true, label: m.label }));
    // the mark covers exactly the look-alike letters (WK.partSpan: the part's label, "ped"), not the model's whole cutting segment ("pede")
    WK.drawStrip(L, st, { gloss: !o.uncut, part: marks.length ? marks.map(m => ({ id: m.id, span: WK.partSpan(e.id, m.id) || undefined, color: m.color, fill: true })) : undefined, alpha: o.alpha });
    // each mark's meaning on a paper chip above its box, in the part's ink (bigger and clearer than the kit's label)
    for (const m of marks) {
      const ls = L.letters.filter(x => x.part === m.id), sp = WK.partSpan(e.id, m.id) || (ls.length ? [ls[0].i, ls[ls.length - 1].i + 1] : null);
      if (!sp || (ls.length && made.length)) continue;     // a cut piece already shows its meaning under it
      const x0 = WK.gapX(L, sp[0], st), x1 = WK.gapX(L, sp[1], st), size = UI.text(32), w = tw(m.label, size) + 34, h = size * 1.45;
      const y = cy - L.th / 2 - 14 - h / 2;
      g.save(); if (o.alpha != null) g.globalAlpha *= o.alpha;
      paperRect((x0 + x1) / 2, y, Math.round(w), Math.round(h), '#fffefa', { seed: 81 + sp[0], depth: 0.8, boil: false });
      txt(m.label, (x0 + x1) / 2, y + 1, size, m.color, 'center');
      g.restore();
    }
    // what the glosses show now (the kit's rule: a piece with exactly one part, typed on at its cut + 0.3 s)
    const glosses = [];
    if (!o.uncut && made.length) {
      const off = WK.offsets(L, st);
      for (const pc of WK.pieces(L, st, off, 0)) {
        if (pc.parts.length === 1 && (pc.t0 < -1e8 || t >= pc.t0 + 0.3)) glosses.push(Words.short(pc.parts[0]));
      }
    }
    return { id: e.id, made: made.slice().sort((a, b) => a - b), model: e.model.slice(), plain: !!L.plain, glosses, marks: marks.map(m => ({ id: m.id, color: m.color, label: m.label })), cy, th: L.th };
  }
  function drawMat(S, t, docked, ended) {
    const M = docked ? MAT_D : MAT, k = lineIdx(S, t), e = k >= 0 ? S.plan[k] : { kind: 'title', last: null, from: 0 };
    const pic = { kind: e.kind, strips: [], tiles: [], tales: [] };
    WK.mat(M.x, M.y, M.w, M.h);
    const gT = T, gBF = BF; T = t; BF = Math.floor(t * 8);
    const a = c01((t - (e.from == null ? -1 : e.from)) / 0.35);
    const mid = M.y + M.h / 2;
    try {
      if (t >= S.voiceEnd + 0.4 && S.finale.length) {
        /* the finale (only the beat plays on): the title, every word the piece took apart, finished, and DECODED lands */
        const title = String(((Game.content && Game.content.mic) || {}).title || 'Take It Apart').toUpperCase(), fa = c01((t - S.voiceEnd - 0.4) / 0.5);
        g.save(); g.globalAlpha *= fa;
        ransom(title, M.x + M.w / 2, M.y + (docked ? 110 : 76), docked ? 100 : 78, S.voiceEnd + 0.4, { seed: 404 });
        const n = S.finale.length, cols = docked ? 2 : 3, rows = Math.ceil(n / cols), top = M.y + (docked ? 220 : 150), bot = M.y + M.h * (docked ? 0.74 : 0.7);
        const rh = (bot - top) / rows, cw = (M.w - 80) / cols;
        S.finale.forEach((x, i) => {
          const r = Math.floor(i / cols), c = i % cols, inRow = Math.min(cols, n - r * cols);
          const cx = M.x + M.w / 2 + (c - (inRow - 1) / 2) * cw, cy = top + rh * (r + 0.5);
          const L = layoutOf(x.id, { x: cx - cw / 2 + 20, w: cw - 40, cy }, { maxPitch: docked ? 60 : 50, maxH: docked ? 70 : 60, cuts: undefined });
          if (!L) return;
          const done = { made: x.made.slice(), cutT: {} };
          WK.drawStrip(L, done, { gloss: false, part: x.marks.length ? x.marks.map(m => ({ id: m.id, span: WK.partSpan(x.id, m.id) || undefined, color: m.color, fill: true })) : undefined });
          pic.strips.push({ id: x.id, made: done.made.slice(), model: x.model.slice(), plain: !!L.plain, glosses: [], marks: x.marks.map(m => ({ id: m.id, color: m.color })), cy, th: L.th });
        });
        g.restore();
        WK.stamp('DECODED', M.x + M.w / 2, M.y + M.h * (docked ? 0.87 : 0.85), { t0: S.decodedAt, size: docked ? 96 : 80, color: WK.PAL.red, rot: -0.06, maxW: M.w - 200 });
        pic.kind = 'finale'; pic.title = title; pic.decoded = t >= S.decodedAt;
      } else if (e.kind === 'strip') {
        if (e.above) {
          const r1 = drawEntryStrip(e.above, t, M, M.y + M.h * 0.33, { maxPitch: 100, maxH: 116 });
          const r2 = drawEntryStrip(e, t, M, M.y + M.h * 0.75, { maxPitch: 100, maxH: 116, alpha: a });
          if (r1) pic.strips.push(r1); if (r2) pic.strips.push(r2);
        } else { const r = drawEntryStrip(e, t, M, mid - 10, { alpha: a }); if (r) pic.strips.push(r); }
      } else if (e.kind === 'tiles') {
        const n = e.tiles.length, w = Math.min(380, (M.w - 200) / n - 60), h = Math.round(w / 1.72), gap = w + 120;
        e.tiles.forEach((tl, i) => {
          const x = M.x + M.w / 2 + (i - (n - 1) / 2) * gap, flip = c01((t - tl.at) / 0.3);
          WK.tile(tl.part, x, mid, { w, h, flip, alpha: a });
          pic.tiles.push({ part: tl.part, back: flip > 0.5, short: flip > 0.5 ? Words.short(tl.part) : null });
        });
      } else if (e.kind === 'tales') {
        const n = e.tales.length, w = Math.min(760, (M.w - 120) / n - 40);
        e.tales.forEach((tl, i) => {
          const x = M.x + M.w / 2 + (i - (n - 1) / 2) * (w + 40);
          g.save(); g.globalAlpha *= a;
          const r = WK.taleCard(tl.id, x, M.y + (docked ? 120 : 60), w, { size: UI.text(docked ? 40 : 32) });
          // the model's verdict, stamped under the card (the kit's own words: TRUE / TALL TALE) on the word that names it
          if (r && tl.verdict) WK.stamp(tl.verdict === 'true' ? 'TRUE' : 'TALL TALE', x, r.y + r.h + UI.text(40) * 1.5, { t0: tl.at, color: tl.verdict === 'true' ? WK.PAL.real : WK.PAL.red, size: UI.text(docked ? 52 : 44), rot: -0.06 });
          g.restore();
          pic.tales.push({ id: tl.id, verdict: tl.verdict, stamped: t >= tl.at });
        });
      } else {
        const title = String(((Game.content && Game.content.mic) || {}).title || 'Take It Apart').toUpperCase();
        const ty = e.last ? mid - M.h * 0.2 : mid;
        g.save(); g.globalAlpha *= a; ransom(title, M.x + M.w / 2, ty, docked ? 110 : 96, e.from == null ? -1e6 : e.from + 0.1, { seed: 404 }); g.restore();
        pic.title = title;
        if (e.last && !ended) {                      // at the end the buttons take this place
          const r = drawEntryStrip({ id: e.last, made: [], cutT: {}, model: [], marks: [] }, t, M, mid + M.h * 0.17, { uncut: true, maxPitch: 84, maxH: 100, alpha: a });
          if (r) pic.strips.push(r);
        }
      }
    } finally { T = gT; BF = gBF; }
    return pic;
  }

  /* ---------------- the karaoke band ---------------- */
  const mcache = new Map();
  function tw(str, size) {
    const k = size + '|' + str; let w = mcache.get(k);
    if (w === undefined) { g.save(); g.font = `${size}px ${TYPE}`; w = g.measureText(str).width; g.restore(); if (!document.fonts || document.fonts.status === 'loaded') { if (mcache.size > 3000) mcache.clear(); mcache.set(k, w); } }
    return w;
  }
  function txt(s, x, y, size, color, align = 'left', alpha = 1) {
    g.save(); g.font = `${Math.round(size)}px ${TYPE}`; g.fillStyle = color; g.textAlign = align; g.textBaseline = 'middle'; g.globalAlpha *= alpha; g.fillText(s, x, y); g.restore();
  }
  function band(S, t) {
    const C = ZC();
    Zine.page(960, BAND_Y + 160, 2040, 330, { seed: 77, dark: 0.25 });
    const { k, cur, next } = lyricAt(S, t), aw = activeWord(S, t);
    const sec = (cur || next || {}).section;
    if (sec) txt('[' + sec.toUpperCase() + ']', 60, BAND_Y + 30, UI.text(24), C.red);
    if (cur) {
      let size = UI.text(54); const tot = tw(cur.words.map(w => w.w).join(' '), size);
      if (tot > 1800) size = Math.floor(size * 1800 / tot);
      lineAt(cur, t, size, aw, k);
    }
    if (next) {
      let size = UI.text(34); const w = tw(next.text, size); if (w > 1760) size = Math.floor(size * 1760 / w);
      txt(next.text, 960, NEXT_Y, size, GREY, 'center');
    }
  }
  function lineAt(cur, t, size, aw, k) {
    const C = ZC(), sp = tw(' ', size), ws = cur.words.map(w => tw(w.w, size));
    const tot = ws.reduce((a, b) => a + b, 0) + sp * (ws.length - 1);
    let x = 960 - tot / 2;
    cur.words.forEach((w, j) => {
      const on = aw && aw.line === k && aw.j === j;
      if (on) Zine.highlight(x - 6, CUR_Y + 2, ws[j] + 12, size * 0.95, '#fff04a', { prog: 1, seed: 90 + j });
      txt(w.w, x, CUR_Y, size, t >= w.s - 0.02 ? C.ink : GREY);
      x += ws[j] + sp;
    });
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class Piece extends Scene {
    enter() {
      this.song = null; this.mode = 'wait'; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0; this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      if (WK.load) WK.load();
      if (WK.warm) WK.warm(['astronaut', 'thermometer', 'benefactor', 'pedestrian', 'pediatrician', 'inflammable', 'helicopter'].filter(has));
      const wurl = Assets.url(WORDS);
      let data = null;
      const words = (window.Loader ? Loader.json(wurl) : fetch(wurl).then(r => (r.ok ? r.json() : null), () => null)).then(d => { data = d; }, () => {});
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); }, () => {});
      Promise.all([words, audio]).then(() => {
        if (this.gone) return;
        let S = null;
        if (data && this.audioReady) { try { S = realSong(data); } catch (e) { this.err = String(e && e.message); } }
        if (S) { this.song = S; this.mode = 'synced'; }
        else { this.song = fallbackSong(); this.mode = 'fallback'; this.audioReady = false; }
        if (!this.frozen && !this.startedOnce) this.play(0);
      });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; this.playing = false; Audio.stopGroup('song');
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    back() { Game.replace(new Levels.map()); }
    /* the piece clock: the <audio> element while it plays (t0ctx != null), else pos (the virtual clock) */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; } else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() { this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1; Audio.stopGroup('song'); if (this.media) this.media.pause(); }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur - 0.05); this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; }
    done() { if (this.completed) return; this.completed = true; if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    finish() { this.done(); }
    canFinish() { return !!this.song && !this.completed && this.heard >= this.need() && this.now() >= this.song.voiceEnd; }
    /* 90 % of the spoken part must have been heard (seeking past it does not count) */
    need() { return THRESH * this.song.voiceEnd; }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    planAt(t) { if (!this.song) return null; const k = lineIdx(this.song, t); return k >= 0 ? this.song.plan[k] : null; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) && (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      { const tn = this.now(); if (tn >= this.song.decodedAt && (this.lastT == null || this.lastT < this.song.decodedAt) && this.heard >= this.need()) Audio.play('stampink', { gain: 0.7 }); this.lastT = tn; }
      if (this.now() >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= this.need()) { Audio.play('stamp', { gain: 0.6 }); this.done(); }
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter') { if (this.ended) this.back(); else if (this.canFinish()) this.done(); }
    }
    stripZone() { return Math.max(34, UI.tap(34)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.finishRect && Math.abs(x - this.finishRect[0]) < this.finishRect[2] / 2 && Math.abs(y - this.finishRect[1]) < this.finishRect[3] / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() { if (!this.scrub) return; const was = this.scrub.was; this.scrub = null; if (was) this.play(this.pos); }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    label() { return this.mode === 'synced' ? CREDIT : this.mode === 'fallback' ? NOREC : 'loading the piece…'; }

    draw() {
      const docked = UI.docked(), title = (Game.lesson.steps.find(s => s.id === STEP) || {}).title || 'TAKE IT APART';
      const D = Piece.dbg = { mode: this.mode, label: this.label(), docked, t: null, line: -1, lineText: null, section: null, word: -1, wordText: null, picture: null, playing: this.playing, ended: this.ended, heard: this.heard };
      Zine.cork();
      if (!this.song) {
        if (!docked) UI.hud(title, '');
        else { Dock.head(title, ''); Dock.text('pc-wait', ['loading the piece…'], { cls: 'note center' }); Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => this.back() }); }
        return;
      }
      const S = this.song, t = clamp(this.now(), 0, S.dur);
      const { k } = lyricAt(S, t), aw = activeWord(S, t);
      D.t = t; D.line = k; D.lineText = k >= 0 ? S.lines[k].text : null; D.section = k >= 0 ? S.lines[k].section : null;
      D.word = aw ? aw.j : -1; D.wordText = aw ? aw.w : null; D.wordS = aw ? aw.s : null;
      D.picture = drawMat(S, t, docked, this.ended && !docked);
      if (docked) { this.dock(t, title); return; }
      band(S, t);
      this.chrome(t, title);
    }

    /* ---------- portrait dock ---------- */
    dock(t, title) {
      const S = this.song, { cur, next } = lyricAt(S, t);
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const sec = (cur || next || {}).section;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) this._dk = { key, words: cur.words.map(w => ({ w: w.w, s: w.s, e: w.e })) };
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '', note: ' ' });
      Dock.text('pc-label', [this.label()], { cls: 'small center' });
      if (this.ended && Game.current() === this) Dock.buttons('pc-end', [{ label: 'Play it again', onClick: () => this.restart() }, { label: 'Back to the map', onClick: () => this.back() }]);
      else if (this.canFinish() && Game.current() === this) Dock.buttons('pc-finish', [{ label: 'Finish', onClick: () => this.done() }]);
      Dock.strip({ value: t, total: S.dur, ticks: S.ticks, label: 'position in the piece', onSeek: (v, phase) => this.scrubTo(v, phase) });
      if (!this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the piece from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => this.back() });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150, 1080);
    }
    /* the paused mark: a dim wash and a highlighter-yellow disc with an ink triangle (a path, never a glyph) */
    pausedMark(y, r, h) {
      g.save(); g.fillStyle = 'rgba(20,18,16,0.3)'; g.fillRect(0, 0, 1920, h); g.restore();
      paperCircle(960, y, r, '#fff04a', { seed: 63, depth: 1.4, boil: false });
      g.save(); g.fillStyle = '#141210'; g.beginPath(); g.moveTo(960 - r * 0.32, y - r * 0.55); g.lineTo(960 + r * 0.55, y); g.lineTo(960 - r * 0.32, y + r * 0.55); g.closePath(); g.fill(); g.restore();
    }
    /* a round HUD control with an ink icon drawn as paths → clicked */
    ctl(id, slot, icon, color) {
      const s = UI.hudSlot(slot), hit = UI._hit('b:' + id, s.x, s.y, s.r * 2, s.r * 2);
      paperCircle(s.x, s.y, s.r, hit.hover ? '#fff04a' : color, { seed: 610 + slot, depth: hit.pressed ? 0.4 : 1, boil: false });
      const r = s.r * 0.4;
      g.save(); g.translate(s.x, s.y); g.fillStyle = '#141210'; g.strokeStyle = '#141210';
      if (icon === 'play') { g.beginPath(); g.moveTo(-r * 0.6, -r); g.lineTo(r, 0); g.lineTo(-r * 0.6, r); g.closePath(); g.fill(); }
      else if (icon === 'pause') { g.fillRect(-r * 0.85, -r, r * 0.6, r * 2); g.fillRect(r * 0.25, -r, r * 0.6, r * 2); }
      else {
        g.lineWidth = Math.max(4, r * 0.28); g.lineCap = 'round'; g.beginPath(); g.arc(0, 0, r * 0.85, -Math.PI * 0.35, Math.PI * 1.25); g.stroke();
        const ax = Math.cos(-Math.PI * 0.35) * r * 0.85, ay = Math.sin(-Math.PI * 0.35) * r * 0.85;
        g.beginPath(); g.moveTo(ax + r * 0.5, ay - r * 0.05); g.lineTo(ax - r * 0.1, ay - r * 0.55); g.lineTo(ax - r * 0.15, ay + r * 0.35); g.closePath(); g.fill();
      }
      g.restore();
      return hit.clicked;
    }
    chrome(t, title) {
      const S = this.song, C = ZC();
      UI.hud(title, '');
      // the honest label: a small paper strip under the title
      { const size = UI.text(24), lab = this.label(), w = tw(lab, size) + 36;
        paperRect(44 + w / 2, 152, w, size * 1.5, this.mode === 'fallback' ? '#fffefa' : '#f6efdc', { seed: 71, depth: 0.8, boil: false });
        txt(lab, 62, 153, size, this.mode === 'fallback' ? C.red : C.grey); }
      if (this.ctl('pc-play', 1, this.playing ? 'pause' : 'play', '#fff04a')) this.toggle();
      if (this.ctl('pc-restart', 2, 'restart', '#fffefa')) this.restart();
      // the position strip (click / drag to seek), section ticks
      const zone = this.stripZone(), touch = zone > 34;
      if (!this.ended) UI.target('pc-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 22 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(20,18,16,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = C.red; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = C.ink; S.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(clamp(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, C.ink, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) {
        this.pausedMark(470, 100, BAND_Y);
        const msg = UI.hint('paused · click or Space to play', 'paused · tap to play'), size = UI.text(40);
        paperRect(960, 640, tw(msg, size) + 50, size * 1.6, '#fffefa', { seed: 64, depth: 0.8, boil: false });
        txt(msg, 960, 641, size, C.ink, 'center');
      }
      this.finishRect = null;
      if (this.ended && Game.current() === this) {
        const bw = UI.tap(440), bh = UI.tap(110);
        if (UI.button('pc-again', 'Play it again', 960 - bw / 2 - 70, MAT.y + MAT.h * 0.83, { w: bw, h: bh, color: '#fff04a', text: '#141210', size: UI.text(46) })) this.restart();
        if (UI.button('pc-back', 'Back to the map', 960 + bw / 2 + 70, MAT.y + MAT.h * 0.83, { w: bw, h: bh, color: C.red, size: UI.text(46) })) this.back();
      } else if (this.canFinish() && Game.current() === this) {
        const m = UI.hudSlot(0), bw = UI.tap(280), bh = UI.tap(84), bx = 1894 - bw / 2, by = m.y + m.r + 24 + bh / 2;
        this.finishRect = [bx, by, bw, bh];
        if (UI.button('pc-finish', 'Finish', bx, by, { w: bw, h: bh, color: C.red, size: UI.text(44) })) this.done();
      }
    }
  }
  Piece.STEP = STEP;
  Piece.KEY = KEY;
  Piece.AUDIO = AUDIO;
  Piece.WORDS = WORDS;
  Piece.NOREC = NOREC;
  Piece.RULES = RULES;
  Piece.realSong = realSong;            // QA: a timing file (house format) → the song the scene draws
  Piece.fallbackSong = fallbackSong;
  Piece.dbg = null;
  /* QA / integration: stamp the step now (as the stub's Finish did) */
  Piece.finish = () => { const s = Game.scenes.find(x => x instanceof Piece); if (s) s.done(); };
  Levels[STEP] = Piece;
})();
